import { type StoreName } from '@bluebrick/discounts-service-module'
import { isValidDate } from '@bluebrick/saas-ui'
import { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useDataPolling } from '@/common/composables/useDataPolling'
import { isNewerDateTime } from '@/common/dateUtils'
import { fetchDiscountsLastScrapeDates } from '@/features/dataHealth/dataHealthRemoteRepository'
import { type ScraperConfigurationStatus, type DiscountsLastScrapeDates, type PortalsStatus, type LastScrapeDate } from '@/features/dataHealth/types'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { type Portal } from '@/types'

const useDataHealthInnerStore = defineStore('dataHealthInner', () => {
	const discountsLastScrapeDates = ref<DiscountsLastScrapeDates<DateTime>>({})
	const portalsStatusMap = ref(new Map<Portal, ScraperConfigurationStatus>())
	const isDataHealthPollingDisabled = ref(false)

	return {
		discountsLastScrapeDates,
		portalsStatusMap,
		isDataHealthPollingDisabled
	}
})

export const useDataHealthStore = defineStore('dataHealth', () => {
	const innerStore = useDataHealthInnerStore()
	const { trackEvent } = useTelemetry()
	const lastUpdateDatePerStore = ref(new Map<StoreName, DateTime<true>>())

	const discountsLastScrapeDates = computed(() => innerStore.discountsLastScrapeDates)
	const portalsStatusMap = computed(() => innerStore.portalsStatusMap)

	const newerDataExistsForStore = computed(() => {
		const result = new Map<StoreName, boolean>()

		// is there a store with last scrape date that wasn't present in stores
		// meaning data from a store was just processed and a last scrape date appeared for new store
		// if so, mark that newer data exists for this store - this will result in a re-fetch of stores and events
		const firstNewStore = Object.keys(discountsLastScrapeDates.value).find((store) => !lastUpdateDatePerStore.value.has(store as StoreName))

		if (firstNewStore) {
			result.set(firstNewStore as StoreName, true)
		}

		for (const [storeName, lastDataUpdateDate] of lastUpdateDatePerStore.value) {
			result.set(storeName, lastDataUpdateDate.isValid && isNewerDateTime(discountsLastScrapeDates.value?.[storeName], lastDataUpdateDate))
		}

		return result
	})

	const isNewerDataNotificationVisible = computed(() => [...newerDataExistsForStore.value.values()].some(Boolean))

	const hasAnyStoresWithDiscountsScraped = computed(() => {
		const discountsLastScrapeDatePerStore = Object.values(discountsLastScrapeDates.value)

		return discountsLastScrapeDatePerStore && discountsLastScrapeDatePerStore.some((date) => date?.isValid)
	})

	const { start: startPolling, stop: stopPolling } = useDataPolling<DiscountsLastScrapeDates<DateTime>>({
		name: 'discountsLastScrapeDates',
		getter: async () => await fetchDiscountsLastScrapeDates(),
		callback: (data) => {
			if (data) {
				updateDates(data, true)
			}
		},
		hasDataChanged,
		interval: 180_000
	})

	async function fetch() {
		const data = await fetchDiscountsLastScrapeDates()

		if (data) {
			updateDates(data, true)
		}
	}

	function hasDataChanged(newData: DiscountsLastScrapeDates<DateTime> | undefined, oldData: DiscountsLastScrapeDates<DateTime> | undefined) {
		if (!newData) {
			return false
		}

		return Object.entries(newData).some(
			([storeName, lastDiscountsScrapeDate]) =>
				lastDiscountsScrapeDate && lastDiscountsScrapeDate.isValid && isNewerDateTime(lastDiscountsScrapeDate, oldData?.[storeName as StoreName])
		)
	}

	function updateDates(dates: DiscountsLastScrapeDates<DateTime>, force?: boolean) {
		if (force || hasDataChanged(dates, discountsLastScrapeDates.value)) {
			trackEvent('app', 'Last scrape dates have changed', {
				data: JSON.stringify(dates),
				flag: force
			})

			innerStore.discountsLastScrapeDates = dates
		} else {
			trackEvent('app', 'Not updating last scrape dates', {
				data: JSON.stringify(dates),
				flag: force
			})
		}
	}

	function updateLastScrapeDates(dates: LastScrapeDate[]) {
		const newDates = (dates ?? []).reduce<DiscountsLastScrapeDates<DateTime>>((acc, item) => {
			if (!item || !isValidDate(item.date)) {
				return acc
			}

			acc[item.storeName as StoreName] = DateTime.fromJSDate(item.date).toUTC()

			return acc
		}, {})

		updateDates(newDates)
	}

	function updatePortalsStatus(statuses: PortalsStatus) {
		innerStore.portalsStatusMap = new Map(
			Object.entries(statuses).map(([portal, portalStatus]) => [portal, portalStatus.status]) as [Portal, ScraperConfigurationStatus][]
		)
	}

	function start() {
		if (innerStore.isDataHealthPollingDisabled) {
			return
		}

		trackEvent('app', 'Starting polling data health.')

		startPolling()
	}

	function stop(permanently?: boolean) {
		trackEvent('app', 'Stopping polling data health.', {
			data: `permanently: ${!!permanently}`
		})

		if (permanently) {
			innerStore.isDataHealthPollingDisabled = true
		}

		stopPolling()
	}

	return {
		start,
		stop,
		fetch,
		updateLastScrapeDates,
		updatePortalsStatus,
		hasAnyStoresWithDiscountsScraped,
		discountsLastScrapeDates,
		lastUpdateDatePerStore,
		portalsStatusMap,
		isNewerDataNotificationVisible
	}
})
